import React, { useState, useEffect } from 'react';
import { cacheAPI, CacheStats } from '../services/api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Snackbar,
} from '@mui/material';
import {
  Refresh,
  Delete,
  DeleteSweep,
  Storage,
  CheckCircle,
  Error as ErrorIcon,
  Info,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

const CacheManagement: React.FC = () => {
  const [cacheNames, setCacheNames] = useState<string[]>([]);
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [clearingCache, setClearingCache] = useState<string | null>(null);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState<boolean>(false);
  const [clearSingleDialogOpen, setClearSingleDialogOpen] = useState<boolean>(false);
  const [selectedCache, setSelectedCache] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadCacheData();
  }, []);

  const loadCacheData = async () => {
    setLoading(true);
    try {
      const [namesResponse, statsResponse] = await Promise.all([
        cacheAPI.getCacheNames(),
        cacheAPI.getCacheStats(),
      ]);
      setCacheNames(namesResponse.data);
      setCacheStats(statsResponse.data);
    } catch (error: any) {
      console.error('Error loading cache data:', error);
      enqueueSnackbar('Failed to load cache data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = async (cacheName: string) => {
    setClearingCache(cacheName);
    try {
      const response = await cacheAPI.clearCache(cacheName);
      enqueueSnackbar(response.data.message, { variant: 'success' });
      await loadCacheData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to clear cache';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setClearingCache(null);
      setClearSingleDialogOpen(false);
      setSelectedCache(null);
    }
  };

  const handleClearAllCaches = async () => {
    setClearingCache('all');
    try {
      const response = await cacheAPI.clearAllCaches();
      enqueueSnackbar(
        `Successfully cleared ${response.data.clearedCount} out of ${response.data.totalCaches} caches`,
        { variant: 'success' }
      );
      await loadCacheData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to clear all caches';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setClearingCache(null);
      setClearAllDialogOpen(false);
    }
  };

  const openClearSingleDialog = (cacheName: string) => {
    setSelectedCache(cacheName);
    setClearSingleDialogOpen(true);
  };

  const openClearAllDialog = () => {
    setClearAllDialogOpen(true);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            Cache Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and clear application caches
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadCacheData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteSweep />}
            onClick={openClearAllDialog}
            disabled={cacheNames.length === 0 || clearingCache !== null}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {cacheStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Storage sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                      {cacheStats.totalCaches}
                    </Typography>
                    <Typography color="text.secondary">Total Caches</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                      {Object.values(cacheStats.caches).filter(c => c.available).length}
                    </Typography>
                    <Typography color="text.secondary">Active Caches</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ErrorIcon sx={{ fontSize: 40, color: '#d32f2f', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                      {Object.values(cacheStats.caches).filter(c => !c.available).length}
                    </Typography>
                    <Typography color="text.secondary">Unavailable Caches</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Cache List */}
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Cache Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cacheNames.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No caches found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                cacheNames.map((cacheName) => {
                  const cacheInfo = cacheStats?.caches[cacheName];
                  const isAvailable = cacheInfo?.available ?? false;
                  const isClearing = clearingCache === cacheName;

                  return (
                    <TableRow key={cacheName} hover>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {cacheName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isAvailable ? 'Active' : 'Unavailable'}
                          color={isAvailable ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {cacheInfo?.nativeCacheType
                            ? cacheInfo.nativeCacheType.split('.').pop() || 'Unknown'
                            : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Clear this cache">
                          <IconButton
                            color="error"
                            onClick={() => openClearSingleDialog(cacheName)}
                            disabled={!isAvailable || isClearing || clearingCache !== null}
                            size="small"
                          >
                            {isClearing ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Delete />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Info Alert */}
      <Alert severity="info" icon={<Info />}>
        <Typography variant="body2">
          <strong>Note:</strong> Clearing caches will remove all cached data. The cache will be
          automatically repopulated as data is accessed. Use with caution during peak usage times.
        </Typography>
      </Alert>

      {/* Clear Single Cache Dialog */}
      <Dialog
        open={clearSingleDialogOpen}
        onClose={() => {
          setClearSingleDialogOpen(false);
          setSelectedCache(null);
        }}
      >
        <DialogTitle>Clear Cache</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear the cache <strong>"{selectedCache}"</strong>? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setClearSingleDialogOpen(false);
              setSelectedCache(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => selectedCache && handleClearCache(selectedCache)}
            color="error"
            variant="contained"
            disabled={clearingCache !== null}
          >
            {clearingCache === selectedCache ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Clearing...
              </>
            ) : (
              'Clear Cache'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Caches Dialog */}
      <Dialog open={clearAllDialogOpen} onClose={() => setClearAllDialogOpen(false)}>
        <DialogTitle>Clear All Caches</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear <strong>all {cacheNames.length} caches</strong>? This
            action cannot be undone and will affect all cached data in the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleClearAllCaches}
            color="error"
            variant="contained"
            disabled={clearingCache !== null}
          >
            {clearingCache === 'all' ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Clearing...
              </>
            ) : (
              'Clear All Caches'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CacheManagement;

